/**
 * SimulationEngine - Main game loop coordinator
 * 
 * Manages:
 * - Time flow and dilation
 * - Multiple life entities
 * - Event generation and distribution
 * - Macro-level effects (collective impact)
 */

import { LifeEntity } from './LifeEntity.js';
import { EventGenerator } from './EventGenerator.js';

export class SimulationEngine {
  constructor(config = {}) {
    this.lives = [];
    this.eventGenerator = new EventGenerator();
    
    // Time control
    this.currentTime = 0;
    this.timeScale = 1.0;        // 1.0 = real-time, 0.5 = half speed, 2.0 = double speed
    this.isPaused = false;
    this.lastUpdateTime = performance.now();
    
    // Macro state
    this.sharedRecoveryPool = 1.0;   // Collective resource (0-1)
    this.systemStrain = 0;            // Increases when lives are struggling
    
    // Configuration
    this.config = {
      maxLives: config.maxLives || 10,
      startWithLives: config.startWithLives || 1,
      enableMacroEffects: config.enableMacroEffects || false,
      ...config
    };
    
    // Initialize starting lives
    this.initializeLives(this.config.startWithLives);
    
    // Animation frame request ID
    this.animationFrameId = null;
  }

  initializeLives(count) {
    for (let i = 0; i < count; i++) {
      this.addLife();
    }
  }

  addLife(config = {}) {
    if (this.lives.length >= this.config.maxLives) return null;
    
    const life = new LifeEntity(this.lives.length, config);
    
    // Randomize initial conditions slightly
    life.zones.home.weatherExposure = 0.3 + Math.random() * 0.4;
    life.zones.auto.trafficDensity = 0.3 + Math.random() * 0.4;
    life.lifestyle.commuteLength = Math.random();
    life.lifestyle.livingDensity = Math.random();
    
    this.lives.push(life);
    return life;
  }

  start() {
    if (this.animationFrameId) return; // Already running
    
    this.isPaused = false;
    this.lastUpdateTime = performance.now();
    this.gameLoop();
  }

  stop() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
    this.lastUpdateTime = performance.now();
  }

  setTimeScale(scale) {
    this.timeScale = Math.max(0.1, Math.min(5.0, scale)); // Clamp between 0.1x and 5x
  }

  gameLoop() {
    const now = performance.now();
    const rawDelta = (now - this.lastUpdateTime) / 1000; // Convert to seconds
    const deltaTime = rawDelta * this.timeScale;
    
    this.lastUpdateTime = now;
    
    if (!this.isPaused && deltaTime > 0 && deltaTime < 0.1) { // Cap at 100ms to prevent large jumps
      this.update(deltaTime);
    }
    
    this.animationFrameId = requestAnimationFrame(() => this.gameLoop());
  }

  update(deltaTime) {
    this.currentTime += deltaTime;
    
    // Update event generator
    this.eventGenerator.update(deltaTime, this.lives);
    
    // Update all lives
    this.lives.forEach(life => life.update(deltaTime));
    
    // Update macro effects (if enabled)
    if (this.config.enableMacroEffects && this.lives.length > 1) {
      this.updateMacroEffects(deltaTime);
    }
  }

  updateMacroEffects(deltaTime) {
    // Calculate system strain based on all lives
    const avgStability = this.lives.reduce((sum, life) => sum + life.stabilityMeter, 0) / this.lives.length;
    this.systemStrain = 1.0 - (avgStability / 100);
    
    // Shared recovery pool affected by system strain
    const targetPool = 0.5 + (avgStability / 100) * 0.5; // 0.5 to 1.0
    this.sharedRecoveryPool += (targetPool - this.sharedRecoveryPool) * 0.1;
    
    // When one life takes a hit, others feel it
    this.lives.forEach(life => {
      // Reduce recovery bandwidth based on system strain
      life.recoveryBandwidth *= (1.0 - this.systemStrain * 0.3);
      
      // Cost accumulation rises when system is strained
      const strainCost = this.systemStrain * 0.5 * deltaTime;
      life.totalCostAccumulation += strainCost;
    });
    
    // If any life is in critical condition, increase strain for all
    const criticalLives = this.lives.filter(life => life.stabilityMeter < 30);
    if (criticalLives.length > 0) {
      const cascadeEffect = criticalLives.length / this.lives.length;
      this.lives.forEach(life => {
        life.stabilityMeter -= cascadeEffect * 2 * deltaTime;
      });
    }
  }

  // External API

  getLife(id) {
    return this.lives.find(life => life.id === id);
  }

  getAllLives() {
    return this.lives.map(life => life.getState());
  }

  getQueuedEvents() {
    return this.eventGenerator.getQueuedEvents();
  }

  setBehavior(lifeId, zone, parameter, value) {
    const life = this.getLife(lifeId);
    if (life) {
      life.setBehavior(zone, parameter, value);
    }
  }

  setCoverage(lifeId, zone, level) {
    const life = this.getLife(lifeId);
    if (life) {
      life.setCoverage(zone, level);
    }
  }

  setLifestyle(lifeId, parameter, value) {
    const life = this.getLife(lifeId);
    if (life) {
      life.setLifestyle(parameter, value);
    }
  }

  injectEvent(eventType, lifeId, delay = 3.0) {
    const life = this.getLife(lifeId);
    if (life) {
      this.eventGenerator.injectEvent(eventType, life, delay);
    }
  }

  getSystemState() {
    return {
      currentTime: this.currentTime,
      timeScale: this.timeScale,
      isPaused: this.isPaused,
      lives: this.getAllLives(),
      queuedEvents: this.getQueuedEvents(),
      macroState: {
        sharedRecoveryPool: this.sharedRecoveryPool,
        systemStrain: this.systemStrain,
        avgStability: this.lives.reduce((sum, life) => sum + life.stabilityMeter, 0) / this.lives.length
      }
    };
  }

  // Macro transition - add more lives
  expandToMacro(additionalLives = 9) {
    const targetCount = Math.min(this.config.maxLives, this.lives.length + additionalLives);
    
    while (this.lives.length < targetCount) {
      this.addLife();
    }
    
    // Enable macro effects
    this.config.enableMacroEffects = true;
  }

  cleanup() {
    this.stop();
    this.lives = [];
  }
}

